import React from 'react';

import { Row } from '~components/table-view';
import { formatDateString } from '~libs/dayjs';
import HoverPopover from '~support-at-home/components/common/HoverPopover';

import { SahCategory, SahService } from '~lookups/models';

// The pointer crosses this column on its way along the row, so the popover only opens once it has
// come to rest - otherwise a sweep across the table fires one per row. Matches the delay the
// service associations list uses for the same reason.
const INFO_HOVER_DELAY_MS = 250;

const formatPeriod = (service: SahService): string => {
  // `formatDateString` is the shared `DD/MMM/YYYY` helper, so this period reads the same as every
  // other date in the app and follows it if the display format is ever changed. dayjs parses a
  // date-only string as LOCAL midnight, so a stored 'YYYY-MM-DD' cannot be shifted a day the way
  // `new Date('2025-03-01')` shifts it to Feb 28 anywhere west of UTC - and at a period boundary
  // that is the difference between one contribution category and the other.
  //
  // Not `formatDayString`, which parses against an explicit 'YYYY-MM-DD HH:mm' and so returns
  // 'Invalid Date' for a value carrying no time - which these, being DATEONLY, never do.
  //
  // Both return '' for a nullish value, which the branching below treats as absent.
  const start = formatDateString(service.start_date);
  const end = formatDateString(service.end_date);

  if (!start && !end) return 'any date';
  if (!start) return `up to ${end}`;
  if (!end) return `from ${start} onwards`;
  return `${start} to ${end}`;
};

/**
 * The lines the popover shows for the service a claim item was recorded against.
 *
 * Contribution category first, because it is the reason this exists: a Support at Home service can
 * now run over more than one period with a different category in each, and the two periods share a
 * name. Without this the SaH Service column shows the same text for both and there is no way to
 * tell which treatment an item will be reported under.
 *
 * The period is what distinguishes them, so it comes second. The department's own identifiers
 * follow, matching the detail the service associations list shows behind its info icon.
 */
const buildMessages = (service: SahService, category?: SahCategory): string[] =>
  [
    `Contribution category: ${category?.name ?? 'Not mapped'}`,
    `Available: ${formatPeriod(service)}`,
    service.sah_id && `Service ID: ${service.sah_id}`,
    service.meta_information?.serviceTypeId && `Service type: ${service.meta_information.serviceTypeId}`,
  ].filter(Boolean as unknown as (value: string | false | null | undefined) => value is string);

interface ComponentProps {
  row: Row;
  services: SahService[];
  categories: SahCategory[];
}

/**
 * The SaH service a claim item was recorded against, with its contribution category on hover.
 *
 * The value itself is the trigger, marked with the same dotted underline a captioned column header
 * carries - the `<abbr>` convention for "there is more to this than the word". An info icon per row
 * was the alternative and is the wrong call here: every populated value in this column has
 * something to say, so an icon on all of them carries no information and just adds a column of
 * glyphs to scan past.
 *
 * An item with no resolved service renders as plain text with no affordance - there is nothing
 * behind it, and a dotted underline promising otherwise would be a lie.
 */
const SahServiceCategoryCell: React.FC<ComponentProps> = ({ row, services, categories }) => {
  const service = services.find(({ id }) => id === row.sah_service_id);
  if (!service) return null;

  const category = categories.find(({ id }) => id === service.category_id);

  // Wrapped in `.cell.readonly`, exactly as getDefaultRenderReadOnly does. This table renders
  // `compact`, where `tr.edit td` carries no padding at all and the cell gutter comes from this
  // wrapper instead - so a custom renderReadOnly that returns bare content sits 4px left of its
  // own column header while every default-rendered cell beside it lines up.
  return (
    <div className="cell readonly">
      <HoverPopover
        id={String(row.id)}
        label={service.name}
        // No `text-decoration-none` here, tempting as it looks against `btn-link`'s underline:
        // Bootstrap declares that utility `!important`, so it also strips the `has-caption` dotted
        // underline this trigger depends on. `has-caption` outranks `btn-link` on its own and paints
        // the dotted rule in place of the solid one.
        triggerBaseClassName="btn btn-link p-0 border-0 align-baseline text-start has-caption"
        triggerClassName="text-body"
        variant="info"
        header="Support at Home service"
        messages={buildMessages(service, category)}
        ariaLabel={`Service detail for ${service.name}`}
        dataCyPrefix="claim-item-sah-service"
        // `top`, matching ColumnHeaderTooltip's captions - the convention this cell is borrowing. The
        // service associations list opens its equivalent to the right because its trigger is in the
        // FIRST column with empty space beside it; here the column sits mid-table, so opening right
        // would lay the popover over Service Type and Error.
        placement="top"
          openDelay={INFO_HOVER_DELAY_MS}
      />
    </div>
  );
};

export default SahServiceCategoryCell;
